import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getPassesUrlByAuthorId } from "@/components/edition/category/components/content/useProductItemHandlers";
import useClaimFreeTrial from "@/components/edition/category/hooks/useClaimFreeTrial";
import useRedirect from "@/hooks/useRedirect";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { Button, Icon } from "@bookips/sds";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { REGISTRATION_PAGE, VIEWER_PAGE } from "src/settings/constant";
import useHandoutProductStore from "src/store/relatedProduct";
import { useDialogStore } from "src/store/useDialogStore";
const FREE_TRIAL_DIALOG_ID = "free-trial-dialog";
const EDITION_CATEGORY_PRODUCTS_KEY = "category-products";
/** 에디션 리스트 캐시 업데이트 헬퍼 */ const updateEditionListCache = (queryClient, productId, licenseId, licensePlan)=>{
    const updater = (product)=>product.id === productId ? {
            ...product,
            freeTrial: product.freeTrial ? {
                ...product.freeTrial,
                eligible: false,
                reason: "already_owned"
            } : undefined,
            licensePlan,
            licenseId,
            isFreeOwned: true
        } : product;
    // 일반 상품 리스트
    queryClient.getQueriesData({
        predicate: (query)=>query.queryKey[0] === EDITION_CATEGORY_PRODUCTS_KEY && !query.queryKey.includes("grouped")
    }).forEach((param)=>{
        let [queryKey, data] = param;
        if (data === null || data === void 0 ? void 0 : data.items) {
            queryClient.setQueryData(queryKey, {
                ...data,
                items: data.items.map(updater)
            });
        }
    });
    // 그룹화된 상품 리스트
    queryClient.getQueriesData({
        predicate: (query)=>query.queryKey[0] === EDITION_CATEGORY_PRODUCTS_KEY && query.queryKey.includes("grouped")
    }).forEach((param)=>{
        let [queryKey, data] = param;
        if (data === null || data === void 0 ? void 0 : data.groups) {
            queryClient.setQueryData(queryKey, {
                ...data,
                groups: data.groups.map((group)=>({
                        ...group,
                        items: group.items.map(updater)
                    }))
            });
        }
    });
};
/**
 * 무료체험 가능한 상품의 구매 모듈 (Case 1)
 * - 무료로 받기 버튼
 * - 패스 구매하고 무제한 이용하기 링크
 */ const PurchaseFreeTrialTab = (param)=>{
    let { product , isLoggedIn , freeTrialData , className  } = param;
    var _freeTrialData_promotion;
    const router = useRouter();
    const queryClient = useQueryClient();
    const { open , close  } = useDialogStore();
    const { setFreeTrialData  } = useHandoutProductStore();
    const { redirectToLogin , redirectTo , loginPath  } = useRedirect();
    const { mutateAsync: claimFreeTrialMutation  } = useClaimFreeTrial();
    const promotionId = (_freeTrialData_promotion = freeTrialData.promotion) === null || _freeTrialData_promotion === void 0 ? void 0 : _freeTrialData_promotion.id;
    // 로그인 유도 다이얼로그
    const openLoginDialog = ()=>{
        const popupTitle = "무료 자료를 받기 위해서는 로그인이 필요해요.";
        open({
            id: FREE_TRIAL_DIALOG_ID,
            type: "confirm",
            props: {
                title: popupTitle,
                confirmText: "회원가입",
                cancelText: "로그인",
                onConfirm: ()=>{
                    mixpanelEvent("Popup Button Clicked", {
                        "Popup Type": "로그인 유도",
                        "Popup Title": popupTitle,
                        "Button Name": "회원가입"
                    });
                    redirectTo(REGISTRATION_PAGE);
                },
                onCancel: ()=>{
                    mixpanelEvent("Popup Button Clicked", {
                        "Popup Type": "로그인 유도",
                        "Popup Title": popupTitle,
                        "Button Name": "로그인"
                    });
                    redirectToLogin();
                },
                contentProps: {
                    onInteractOutside: ()=>{
                        close(FREE_TRIAL_DIALOG_ID);
                    }
                }
            }
        });
    };
    // 무료로 받기 확인 다이얼로그
    const openFreeTrialConfirmDialog = ()=>{
        const confirmPopupTitle = "이 자료를 무료로 받을까요?";
        mixpanelEvent("Free Promotion Popup Viewed", {
            "Popup Type": "무료 자료 획득 유도",
            "Popup Title": confirmPopupTitle
        });
        open({
            id: FREE_TRIAL_DIALOG_ID,
            type: "confirm",
            props: {
                title: confirmPopupTitle,
                confirmText: "무료로 받기",
                cancelText: "취소",
                onConfirm: async ()=>{
                    mixpanelEvent("Popup Button Clicked", {
                        "Popup Type": "무료 자료 획득 유도",
                        "Popup Title": confirmPopupTitle,
                        "Button Name": "무료로 받기"
                    });
                    try {
                        const response = await claimFreeTrialMutation({
                            productId: product.id.toString(),
                            promotionId: promotionId
                        });
                        if (!response.success) {
                            throw new Error("API 응답 실패");
                        }
                        const licenseId = response.license.id;
                        if (!licenseId) {
                            return;
                        }
                        const licensePlan = response.license.purchaseOption === "view" ? "view" : "download";
                        // 무료획득 완료 후 UI 업데이트
                        const updatedFreeTrialData = {
                            isEligible: false,
                            reason: "already_owned",
                            isFreeOwned: true,
                            promotion: null,
                            licenseId
                        };
                        // 1. React Query 캐시 업데이트 (상품 상세 페이지용)
                        queryClient.setQueriesData({
                            queryKey: [
                                "freeTrial",
                                "check",
                                product.id.toString()
                            ]
                        }, updatedFreeTrialData);
                        // 2. Zustand store 업데이트 (샘플보기 모달용)
                        setFreeTrialData(updatedFreeTrialData);
                        // 3. 에디션 리스트 캐시 업데이트
                        updateEditionListCache(queryClient, product.id.toString(), licenseId, licensePlan);
                        const successPopupTitle = "무료 체험 자료를 받았어요! 바로 열람 할까요?";
                        mixpanelEvent("Get Free Handout Completed", {
                            "Popup Type": "무료 자료 획득 완료",
                            "Popup Title": successPopupTitle
                        });
                        // 성공 다이얼로그 표시
                        open({
                            id: FREE_TRIAL_DIALOG_ID,
                            type: "confirm",
                            props: {
                                icon: /*#__PURE__*/ _jsx("div", {
                                    className: "py-0.5",
                                    children: /*#__PURE__*/ _jsx(Icon.Success_fill, {
                                        className: "text-status-data size-6"
                                    })
                                }),
                                title: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        "무료 체험 자료를 받았어요!",
                                        /*#__PURE__*/ _jsx("br", {}),
                                        "바로 열람 할까요?"
                                    ]
                                }),
                                confirmText: "자료 열람하기",
                                cancelText: "닫기",
                                onConfirm: ()=>{
                                    const viewerUrl = "".concat(VIEWER_PAGE, "/").concat(licenseId);
                                    mixpanelEvent("Popup Button Clicked", {
                                        "Popup Type": "무료 자료 열람 유도",
                                        "Popup Title": successPopupTitle,
                                        "Button Name": "자료 열람하기",
                                        "Button URL": viewerUrl
                                    });
                                    router.push(viewerUrl);
                                },
                                onCancel: ()=>{
                                    mixpanelEvent("Popup Button Clicked", {
                                        "Popup Type": "무료 자료 열람 유도",
                                        "Popup Title": successPopupTitle,
                                        "Button Name": "닫기"
                                    });
                                },
                                classes: {
                                    dialogTitle: {
                                        titleContainer: "md:items-start"
                                    }
                                },
                                contentProps: {
                                    onInteractOutside: ()=>{
                                        close(FREE_TRIAL_DIALOG_ID);
                                    }
                                }
                            }
                        });
                    } catch (error) {
                        ;
                    }
                },
                onCancel: ()=>{
                    mixpanelEvent("Popup Button Clicked", {
                        "Popup Type": "무료 자료 획득 유도",
                        "Popup Title": confirmPopupTitle,
                        "Button Name": "취소"
                    });
                },
                contentProps: {
                    onInteractOutside: ()=>{
                        close(FREE_TRIAL_DIALOG_ID);
                    }
                }
            }
        });
    };
    // 무료로 받기 버튼 클릭 핸들러
    const handleFreeTrialClick = ()=>{
        mixpanelEvent("CTA Button Clicked", {
            "Button Type": "case1)무료체험 유도할 유저",
            "Button Name": "무료로 받기",
            Position: "상품상세페이지 구매모듈"
        });
        if (!isLoggedIn) {
            openLoginDialog();
            return;
        }
        if (promotionId) {
            openFreeTrialConfirmDialog();
        }
    };
    // 무제한 이용하기 클릭 핸들러
    const handlePurchaseClick = ()=>{
        const passesUrl = getPassesUrlByAuthorId(product.author_id);
        mixpanelEvent("CTA Button Clicked", {
            "Button Type": "case1)무료체험 유도할 유저",
            "Button Name": "패스 구매하고 무제한 이용하기",
            "Button URL": passesUrl,
            Position: "상품상세페이지 구매모듈"
        });
        window.open(passesUrl, "_blank");
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("flex flex-col gap-2", className),
        children: [
            /*#__PURE__*/ _jsx(Button, {
                variant: "fill",
                color: "primary",
                size: "lg",
                fullWidth: true,
                onClick: handleFreeTrialClick,
                children: "무료로 받기"
            }),
            /*#__PURE__*/ _jsx(Button, {
                variant: "text",
                color: "primary",
                size: "sm",
                fullWidth: true,
                rightElement: /*#__PURE__*/ _jsx(Icon.ArrowRight_2px, {
                    className: "text-primary-main size-4.5"
                }),
                onClick: handlePurchaseClick,
                children: "패스 구매하고 무제한 이용하기"
            })
        ]
    });
};
export default PurchaseFreeTrialTab;
